<script setup>
/**
 * The campaign image editor, shared by the Design tab and the preview
 * popover. Two sources:
 *
 * - Custom: paste an image URL or upload a file to account storage
 *   (`/api/v1/accounts/:id/upload`), which fills the URL in.
 * - Library: curated gradient presets stored as `preset:<name>` tokens in
 *   `image_url` (see `shared/helpers/campaignImagePresets.js`).
 *
 * Alt text rides along in `content.image_alt` for either source.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  checkFileSizeLimit,
} from 'shared/helpers/FileHelper';
import {
  CAMPAIGN_IMAGE_PRESETS,
  PRESET_IMAGE_PREFIX,
  isPresetImage,
} from 'shared/helpers/campaignImagePresets';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const imageUrl = defineModel('imageUrl', { type: String, default: '' });
const imageAlt = defineModel('imageAlt', { type: String, default: '' });

const { t } = useI18n();

const MODES = ['custom', 'library'];
const mode = ref(isPresetImage(imageUrl.value) ? 'library' : 'custom');

const isUploading = ref(false);
const fileInput = ref(null);

const customUrl = computed({
  get: () => (isPresetImage(imageUrl.value) ? '' : imageUrl.value),
  set: value => {
    imageUrl.value = value;
  },
});

const presets = Object.entries(CAMPAIGN_IMAGE_PRESETS).map(
  ([name, gradient]) => ({
    name,
    gradient,
    token: `${PRESET_IMAGE_PREFIX}${name}`,
  })
);

const pickPreset = token => {
  // Re-picking the active preset clears it, so the library needs no
  // separate "no image" tile.
  imageUrl.value = imageUrl.value === token ? '' : token;
};

// `accept="image/*"` only filters the picker — a drag-and-drop or a renamed
// file still reaches here, and the upload endpoint accepts anything on the
// file path. Check before spending the round trip.
const handleFileSelect = async event => {
  const [file] = event.target.files ?? [];
  if (!file) return;

  const reset = () => {
    event.target.value = '';
  };

  if (!file.type.startsWith('image/')) {
    useAlert(t('CAMPAIGN.BUILDER.DESIGN.IMAGE.UPLOAD_TYPE_ERROR'));
    reset();
    return;
  }

  if (!checkFileSizeLimit(file, DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE)) {
    useAlert(
      t('CAMPAIGN.BUILDER.DESIGN.IMAGE.UPLOAD_SIZE_ERROR', {
        size: DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
      })
    );
    reset();
    return;
  }

  isUploading.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    imageUrl.value = fileUrl;
  } catch (error) {
    useAlert(t('CAMPAIGN.BUILDER.DESIGN.IMAGE.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
    reset();
  }
};

const modeChipClasses = isActive => [
  'flex-1 h-7 rounded-md font-medium text-label-small transition-colors',
  isActive
    ? 'bg-n-solid-active text-n-slate-12 shadow-sm outline outline-1 outline-n-container'
    : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12',
];

const presetTileClasses = isActive => [
  'h-12 rounded-lg transition-shadow',
  isActive
    ? 'outline outline-2 outline-offset-2 outline-n-violet-9'
    : 'hover:outline hover:outline-2 hover:outline-offset-2 hover:outline-n-slate-6',
];
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      role="group"
      :aria-label="t('CAMPAIGN.BUILDER.DESIGN.IMAGE.MODE_GROUP_LABEL')"
      class="flex items-center gap-1 p-1 border rounded-lg border-n-weak bg-n-alpha-black2"
    >
      <button
        v-for="option in MODES"
        :key="option"
        type="button"
        :aria-pressed="mode === option"
        :class="modeChipClasses(mode === option)"
        @click="mode = option"
      >
        {{ t(`CAMPAIGN.BUILDER.DESIGN.IMAGE.MODE.${option.toUpperCase()}`) }}
      </button>
    </div>

    <template v-if="mode === 'custom'">
      <Input
        v-model="customUrl"
        type="url"
        :label="t('CAMPAIGN.BUILDER.CONTENT.IMAGE_URL.LABEL')"
        :placeholder="t('CAMPAIGN.BUILDER.CONTENT.IMAGE_URL.PLACEHOLDER')"
      />
      <div class="flex items-center gap-2">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-upload"
          :label="t('CAMPAIGN.BUILDER.DESIGN.IMAGE.UPLOAD')"
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="fileInput?.click()"
        />
        <span class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.BUILDER.DESIGN.IMAGE.UPLOAD_HINT') }}
        </span>
      </div>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleFileSelect"
      />
    </template>

    <div
      v-else
      role="group"
      :aria-label="t('CAMPAIGN.BUILDER.DESIGN.IMAGE.PRESET_GROUP_LABEL')"
      class="grid grid-cols-3 gap-2"
    >
      <button
        v-for="preset in presets"
        :key="preset.name"
        type="button"
        :class="presetTileClasses(imageUrl === preset.token)"
        :style="{ backgroundImage: preset.gradient }"
        :aria-pressed="imageUrl === preset.token"
        :aria-label="
          t(
            `CAMPAIGN.BUILDER.DESIGN.IMAGE.PRESETS.${preset.name.toUpperCase()}`
          )
        "
        @click="pickPreset(preset.token)"
      />
    </div>

    <Input
      v-model="imageAlt"
      :label="t('CAMPAIGN.BUILDER.DESIGN.IMAGE.ALT.LABEL')"
      :placeholder="t('CAMPAIGN.BUILDER.DESIGN.IMAGE.ALT.PLACEHOLDER')"
    />

    <Button
      v-if="imageUrl"
      variant="ghost"
      color="ruby"
      size="xs"
      icon="i-lucide-trash"
      :label="t('CAMPAIGN.BUILDER.DESIGN.IMAGE.REMOVE')"
      class="self-start"
      @click="imageUrl = ''"
    />
  </div>
</template>
